import { create } from "zustand";
import { persist } from "zustand/middleware";

interface WishlistItem {
  id: string;
  slug: string;
  name: string;
  image_url: string;
  price: number;
}

type DatabaseCallback = (modelId: string) => Promise<void>;

interface WishlistStore {
  items: WishlistItem[];
  isOpen: boolean;
  // Database callbacks (set by useWishlistSync)
  onAddToDatabase: DatabaseCallback | null;
  onRemoveFromDatabase: DatabaseCallback | null;
  // Actions
  addItem: (item: WishlistItem) => void;
  removeItem: (id: string) => void;
  toggleItem: (item: WishlistItem) => void;
  hasItem: (id: string) => boolean;
  clearItems: () => void;
  setItems: (items: WishlistItem[]) => void;
  setOpen: (open: boolean) => void;
  // Set database callbacks
  setDatabaseCallbacks: (
    onAdd: DatabaseCallback | null,
    onRemove: DatabaseCallback | null
  ) => void;
}

export const useWishlistStore = create<WishlistStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      onAddToDatabase: null,
      onRemoveFromDatabase: null,
      addItem: (item) => {
        const state = get();
        if (state.items.some((i) => i.id === item.id)) return;
        
        // Sync to database if callback is set
        if (state.onAddToDatabase) {
          state.onAddToDatabase(item.id).catch(console.error);
        }
        
        set({ items: [...state.items, item] });
      },
      removeItem: (id) => {
        const state = get();
        
        // Sync to database if callback is set
        if (state.onRemoveFromDatabase) {
          state.onRemoveFromDatabase(id).catch(console.error);
        }
        
        set({ items: state.items.filter((item) => item.id !== id) });
      },
      toggleItem: (item) => {
        const hasItem = get().items.some((i) => i.id === item.id);
        if (hasItem) {
          get().removeItem(item.id);
        } else {
          get().addItem(item);
        }
      },
      hasItem: (id) => get().items.some((i) => i.id === id),
      clearItems: () => {
        const state = get();
        
        // Remove all from database if callback is set
        if (state.onRemoveFromDatabase) {
          state.items.forEach((item) => {
            state.onRemoveFromDatabase!(item.id).catch(console.error);
          });
        }
        
        set({ items: [] });
      },
      setItems: (items) => set({ items }),
      setOpen: (open) => set({ isOpen: open }),
      setDatabaseCallbacks: (onAdd, onRemove) =>
        set({ onAddToDatabase: onAdd, onRemoveFromDatabase: onRemove }),
    }),
    {
      name: "wishlist-storage",
      partialize: (state) => ({ items: state.items }), // Only persist items, not callbacks
    }
  )
);
